import React, { useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import Collapse from '@material-ui/core/Collapse';
import Tabs from '@material-ui/core/Tabs';
import moment from 'moment-timezone';
import SolvCalendar from '../../../SolvPatternLibrary/Calendar';
import Day from '../components/Day';
import SlotSelectorStates from '../components/SlotSelectorStates';
import SolvTabs from '../../../SolvPatternLibrary/Tabs';
import { colors } from '../../../../constants/colors';
import { fontFamily, fontSize } from '../../../../constants/text';
import {
  MORE_DATES_SLOT_SELECTOR,
  NEXT_DAY_BOOKINGS_SLOT_SELECTOR,
  shouldShowFeature,
} from '../../../../core/util/customization';
import { EN, SupportedLocale } from '../../../../core/util/localizedStr';
import { usePalette } from '~/hooks/usePalette';

const StyledTabs = styled(SolvTabs).withConfig({ componentId: 'sc-qkuq0o-0' })<{ bgColor: string }>`
  width: 90%;
  margin: 8px 5% 0;
  background-color: ${colors.white};
  & span {
    height: 2px;
    font-family: ${fontFamily.medium};
  }
  & button {
    text-align: center;
    width: ${({ length }) => Math.round(100 / length)}%;
    font-size: ${fontSize.small};
    min-width: unset;
    white-space: nowrap;
  }
  & .MuiTabs-indicator {
    background-color: ${(props) => props.bgColor} !important;
`;

const Content = styled.div.withConfig({ componentId: 'sc-qkuq0o-1' })`
  width: 90%;
  padding-top: 24px;
  border-top: 1px solid ${colors.grayArea};
  margin: 0 auto 15px;
`;

const CalendarContainer = styled.div.withConfig({ componentId: 'sc-qkuq0o-2' })`
  margin-bottom: 8px;
  overflow: hidden;
`;

type Props = {
  isMoreDate: boolean;
  selectedDate: any;
  location: any;
  setAndHandleDate: (...args: any[]) => any;
  futureSlotData: any;
  handleSelectSlot: (...args: any[]) => any;
  nextDate: any;
  selectedSlots: any[];
  setIsMoreDate: (...args: any[]) => any;
  shouldShowWalkInCopy?: boolean;
  locale?: SupportedLocale;
  isCTA?: boolean;
  // below propsa are for old booking flow
  shouldSelectFirstSlotAsInitialValue: boolean;
};

const TabView = (props: Props) => {
  const {
    isMoreDate,
    selectedDate,
    location,
    setAndHandleDate,
    futureSlotData,
    handleSelectSlot,
    nextDate,
    selectedSlots,
    shouldShowWalkInCopy,
    locale = EN,
    isCTA = false,
    // below props are for old booking flow
    shouldSelectFirstSlotAsInitialValue,
  } = props;

  const { t } = useTranslation();

  const today = moment.tz(location.timeZone);
  const tomorrow = moment.tz(location.timeZone).add(1, 'day');
  const dayAfterTomorrow = moment.tz(location.timeZone).add(2, 'day');

  const { palette } = usePalette();

  const tabs = [
    {
      label: t('today.uppercase'),
      onClick: () =>
        setAndHandleDate({
          selectedDate: today,
          nextDate: tomorrow,
          isMoreDate: false,
        }),
    },
  ];

  if (shouldShowFeature(NEXT_DAY_BOOKINGS_SLOT_SELECTOR, location)) {
    tabs.push({
      label: t('tomorrow.uppercase'),
      onClick: () => {
        setAndHandleDate({
          selectedDate: tomorrow,
          nextDate: dayAfterTomorrow,
          isMoreDate: false,
        });
      },
    });
  }

  if (
    shouldShowFeature(NEXT_DAY_BOOKINGS_SLOT_SELECTOR, location) &&
    shouldShowFeature(MORE_DATES_SLOT_SELECTOR, location)
  ) {
    tabs.push({
      label: t('bookingTimeSelector.slotSelectorStates.moreDates'),
      onClick: () => setAndHandleDate({ isMoreDate: true }),
    });
  }

  const activeTabIndex = useMemo(() => {
    if (isMoreDate) {
      return 2;
    }
    if (selectedDate.isSame(today, 'day')) {
      return 0;
    }
    if (selectedDate.isSame(tomorrow, 'day')) {
      return 1;
    }
    return 0;
  }, [isMoreDate, selectedDate, today, tomorrow]);

  return (
    <div>
      <StyledTabs
        bgColor={palette.base}
        isCentered
        length={tabs.length}
        selectedTabIndex={activeTabIndex}
        styledTabs={Tabs}
        tabs={tabs}
      />

      <Content>
        <Collapse in={isMoreDate} mountOnEnter timeout="auto">
          <CalendarContainer>
            <SolvCalendar
              handleDateChange={(selectedDate: any) => setAndHandleDate({ selectedDate })}
              locale={locale}
              location={location}
              selectedDate={selectedDate}
            />
          </CalendarContainer>
        </Collapse>

        <Day
          isMoreDate={isMoreDate}
          locale={locale}
          selectedDate={selectedDate}
          timeZone={location.timeZone}
        />

        <SlotSelectorStates
          allowExpandInPlace
          futureSlotData={futureSlotData}
          handleSelectSlot={handleSelectSlot}
          isCTA={isCTA}
          location={location}
          nextDate={nextDate}
          selectedDate={selectedDate}
          selectedSlots={selectedSlots}
          shouldSelectFirstSlotAsInitialValue={shouldSelectFirstSlotAsInitialValue}
          shouldSetSelectedSlotToFirstVisibleSlot={false}
          shouldShowNextAvailableAppointmentCta={false}
          shouldShowWalkInCopy={shouldShowWalkInCopy}
        />
      </Content>
    </div>
  );
};

export default TabView;
